import { Link } from 'react-router-dom';
import { Heart, MapPin, Maximize2, BedDouble, Bath, ExternalLink, ImageOff, UserPlus } from 'lucide-react';
import { Card } from '../ui/Card';
import { cn, formatPrice, formatSurface, formatRelativeDate, getPlatformLabel } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth';
import { api, type Listing } from '../../services/api';
import { toast } from '../ui/Toast';
import { useState, useCallback, memo } from 'react';

interface PropertyCardProps {
  listing: Listing;
  onFavoriteToggle?: (listingId: string, isFavorite: boolean) => void;
  onSaveAsLead?: (listing: Listing) => void;
  isHighlighted?: boolean;
  className?: string;
}

export const PropertyCard = memo(function PropertyCard({ listing, onFavoriteToggle, onSaveAsLead, isHighlighted, className }: PropertyCardProps) {
  const { isAuthenticated } = useAuthStore();
  const [isFavorite, setIsFavorite] = useState(listing.isFavorite || false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast({ title: 'Accedi per salvare i preferiti', variant: 'warning' });
      return;
    }

    setIsLoading(true);
    try {
      if (isFavorite) {
        await api.removeFavorite(listing.id);
        setIsFavorite(false);
        onFavoriteToggle?.(listing.id, false);
        toast({ title: 'Rimosso dai preferiti', variant: 'default' });
      } else {
        await api.addFavorite(listing.id);
        setIsFavorite(true);
        onFavoriteToggle?.(listing.id, true);
        toast({ title: 'Aggiunto ai preferiti', variant: 'success' });
      }
    } catch {
      toast({
        title: 'Impossibile completare l\'operazione',
        description: 'Riprova piu tardi',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  // Handle both string URLs and {url, label} objects for images
  const firstImage = listing.images?.[0];
  const mainImage = typeof firstImage === 'string'
    ? firstImage
    : firstImage?.url;
  const hasValidImage = mainImage && !imageError;

  // Format date safely - handle undefined/null createdAt
  const formattedDate = listing.createdAt ? formatRelativeDate(listing.createdAt) : null;

  return (
    <Card className={cn('overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-all group border-0 bg-white', isHighlighted && 'ring-2 ring-blue-500', className)}>
      <Link to={`/listings/${listing.id}`} className="flex flex-col h-full">
        {/* Image Container - Aspect ratio 4:3 */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          {/* Loading placeholder with subtle animation */}
          {!imageLoaded && hasValidImage && (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
          )}

          {/* Actual image or placeholder */}
          {hasValidImage ? (
            <img
              src={mainImage}
              alt={listing.title}
              className={cn(
                'h-full w-full object-cover transition-all duration-500 group-hover:scale-105',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
          ) : (
            <div className="h-full w-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
              <ImageOff className="h-10 w-10 mb-2" />
              <span className="text-xs">Immagine non disponibile</span>
            </div>
          )}

          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

          {/* Price badge - Bottom left, prominent */}
          <div className="absolute bottom-3 left-3">
            {listing.price ? (
              <span className="rounded-xl bg-white px-3 py-2 text-base font-bold text-gray-900 shadow-md">
                {formatPrice(listing.price, listing.contractType)}
              </span>
            ) : (
              <span className="rounded-xl bg-white/95 px-3 py-2 text-sm font-medium text-gray-600 shadow-md">
                Prezzo su richiesta
              </span>
            )}
          </div>

          {/* Top badges - Tipo contratto e piattaforma */}
          <div className="absolute left-3 top-3 flex gap-2">
            {isHighlighted && (
              <span className="rounded-lg px-2.5 py-1 text-xs font-semibold shadow-sm bg-orange-500 text-white flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                </span>
                Nuovo
              </span>
            )}
            <span
              className={cn(
                'rounded-lg px-2.5 py-1 text-xs font-semibold shadow-sm',
                listing.contractType === 'SALE'
                  ? 'bg-blue-500 text-white'
                  : 'bg-green-500 text-white'
              )}
            >
              {listing.contractType === 'SALE' ? 'Vendita' : 'Affitto'}
            </span>
          </div>

          {/* Action buttons - Top right */}
          <div className="absolute right-2 top-2 flex flex-col gap-2">
            <button
              onClick={handleFavoriteClick}
              disabled={isLoading}
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-full transition-all',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2',
                'active:scale-90',
                isFavorite
                  ? 'bg-red-500 text-white shadow-md'
                  : 'bg-white/95 text-gray-600 shadow-md hover:bg-white',
                isLoading && 'opacity-50 cursor-wait'
              )}
              aria-label={isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
            >
              <Heart
                className={cn(
                  'h-5 w-5 transition-transform',
                  isFavorite && 'fill-current scale-110'
                )}
              />
            </button>
            {onSaveAsLead && isAuthenticated && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSaveAsLead(listing);
                }}
                className={cn(
                  'flex h-11 w-11 items-center justify-center rounded-full transition-all',
                  'bg-white/95 text-gray-600 shadow-md hover:bg-blue-50 hover:text-blue-600',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2',
                  'active:scale-90'
                )}
                aria-label="Salva come Lead"
              >
                <UserPlus className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col">
          {/* Title - Max 2 lines */}
          <h3 className="mb-2 line-clamp-2 text-base font-semibold text-gray-900 leading-snug group-hover:text-blue-600 transition-colors">
            {listing.title}
          </h3>

          {/* Location */}
          <div className="mb-3 flex items-center text-sm text-gray-500">
            <MapPin className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <span className="truncate">
              {listing.address || listing.city}
              {listing.province && `, ${listing.province}`}
            </span>
          </div>

          {/* Key features - Horizontal chips */}
          <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-3">
            {listing.surface && (
              <span className="inline-flex items-center bg-gray-100 rounded-md px-2 py-1">
                <Maximize2 className="mr-1 h-3.5 w-3.5 text-gray-500" />
                <span className="font-medium">{formatSurface(listing.surface)}</span>
              </span>
            )}
            {listing.rooms && (
              <span className="inline-flex items-center bg-gray-100 rounded-md px-2 py-1">
                <BedDouble className="mr-1 h-3.5 w-3.5 text-gray-500" />
                <span className="font-medium">{listing.rooms} loc.</span>
              </span>
            )}
            {listing.bathrooms && (
              <span className="inline-flex items-center bg-gray-100 rounded-md px-2 py-1">
                <Bath className="mr-1 h-3.5 w-3.5 text-gray-500" />
                <span className="font-medium">{listing.bathrooms}</span>
              </span>
            )}
          </div>

          {/* Footer - Meta info */}
          <div className="mt-auto pt-3 flex items-center justify-between border-t border-gray-100">
            <div className="flex items-center gap-2">
              {formattedDate && (
                <>
                  <span className="text-xs text-gray-400">
                    {formattedDate}
                  </span>
                  <span className="text-xs text-gray-300">|</span>
                </>
              )}
              <span className="text-xs text-gray-400">
                {getPlatformLabel(listing.platform)}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(listing.sourceUrl, '_blank', 'noopener,noreferrer');
              }}
              className="flex items-center text-xs text-blue-500 hover:text-blue-600 py-1.5 px-2.5 -mr-2 rounded-lg hover:bg-blue-50 transition-colors min-h-[32px]"
              aria-label="Vedi annuncio originale"
            >
              <ExternalLink className="mr-1 h-3 w-3" />
              Originale
            </button>
          </div>
        </div>
      </Link>
    </Card>
  );
});

export function PropertyCardSkeleton() {
  return (
    <Card className="overflow-hidden rounded-2xl shadow-sm border-0 bg-white">
      <div className="flex flex-col">
        {/* Image Skeleton with gradient */}
        <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />

        {/* Content Skeleton */}
        <div className="p-4 flex flex-col">
          {/* Title Skeleton */}
          <div className="h-5 w-3/4 bg-gray-200 rounded-md animate-pulse mb-2" />
          <div className="h-4 w-1/2 bg-gray-200 rounded-md animate-pulse mb-3" />

          {/* Location Skeleton */}
          <div className="h-4 w-2/3 bg-gray-200 rounded-md animate-pulse mb-3" />

          {/* Features Skeleton */}
          <div className="flex gap-2 mb-3">
            <div className="h-7 w-16 bg-gray-200 rounded-md animate-pulse" />
            <div className="h-7 w-14 bg-gray-200 rounded-md animate-pulse" />
            <div className="h-7 w-10 bg-gray-200 rounded-md animate-pulse" />
          </div>

          {/* Footer Skeleton */}
          <div className="mt-auto pt-3 flex items-center justify-between border-t border-gray-100">
            <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>
    </Card>
  );
}
